import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { downloadURL } from "../react-helpers/url";
import { loggerBuilder } from "../services/logger";
import { FFmpeg, createFFmpeg } from "@ffmpeg/ffmpeg";
import fixWebmDuration from "webm-duration-fix";
import { toastsWithIntl } from "./toastService";
import { useNavigate } from "react-router-dom";
import { useConfirmationWithIntl } from "../components/ConfirmationDialog";
import { useTranslation } from "react-i18next";

declare global {
  interface Window {
    // Global because on memory constrained devices we can be sure to load it only once
    ffmpeg: FFmpeg | null;
  }
}

export const MAX_HEIGHT = 720;
export const FRAMERATE = 24;

const logger = loggerBuilder("recorder");
const { toastError } = toastsWithIntl(["record"]);

/** ffmpeg.wasm could not be loaded, mostly a memory allocation failure on low-end devices */
export class FFmpegLoadError extends Error {
  constructor(readonly reason: unknown) {
    super(`Failed to load ffmpeg: ${String(reason)}`);
    this.name = "FFmpegLoadError";
  }
}

// SOURCE: https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
function toastGetUserMediaError(e: unknown) {
  switch ((e as { name?: string } | null)?.name) {
    case "NotAllowedError":
    case "SecurityError":
      return toastError("record:record.NOT_ALLOWED", {
        id: "record.NOT_ALLOWED",
      });
    case "NotFoundError":
    case "OverconstrainedError":
      return toastError("record:record.NO_VIDEO", { id: "record.NO_VIDEO" });
    case "NotReadableError":
    case "AbortError":
      return toastError("record:record.COULD_NOT_START", {
        id: "record.COULD_NOT_START",
      });
    default:
      logger.error(e);
      return toastError("record:record.COULD_NOT_START", {
        id: "record.COULD_NOT_START",
      });
  }
}

interface RecorderService {
  timeElapsed: number | null;
  recording: boolean;
  ffmpegPercent: number | null;
  isRecorderReady: boolean;

  startRecorder(): Promise<MediaStream>;
  stopRecorder(): void;

  onStop(): Promise<Blob>;
  /** @returns whether the recording actually started */
  onStart(): boolean;
  onExport(filename: string): void;
  onCompress(video?: Blob, duration?: number): Promise<Blob>;
  /** @returns what `getUploadLink` resolved, once the PUT succeeded */
  onUpload<T extends { uploadLink: string }>(
    getUploadLink: () => Promise<T>,
    video?: Blob,
    duration?: number,
  ): Promise<T>;
}

const RecorderContext = createContext<RecorderService | null>(null);

function useMediaRecorder() {
  const { toastError } = toastsWithIntl(["record"]);

  if (window.MediaRecorder) return window.MediaRecorder;
  else {
    toastError("record:NO_MEDIA_RECORDER", {
      id: "record.NO_MEDIA_RECORDER",
    });
    throw new Error("No MediaRecorder found");
  }
}

function useProvideRecorder(
  timerInterval = 1000,
  chunkDuration = 100,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _maxUncompressedSize = 40 * 1024 * 1024,
): RecorderService {
  // HACK: On really old devices MediaRecorder does not exist :)
  const MediaRecorder = useMediaRecorder();
  const { confirm } = useConfirmationWithIntl(["record"]);
  const { t } = useTranslation(["record"]);
  const navigate = useNavigate();

  // Initialize ffmpeg
  const ffmpegLoadingRef = useRef<Promise<FFmpeg> | null>(null);
  const loadFFmpeg = useCallback(() => {
    window.ffmpeg ??= createFFmpeg();
    const ffmpeg = window.ffmpeg;
    if (ffmpeg.isLoaded()) return Promise.resolve(ffmpeg);

    ffmpegLoadingRef.current ??= ffmpeg.load().then(
      () => ffmpeg,
      (e) => {
        // Forget the failed attempt so the next call retries from scratch
        ffmpegLoadingRef.current = null;
        window.ffmpeg = null;
        throw new FFmpegLoadError(e);
      },
    );
    return ffmpegLoadingRef.current;
  }, []);
  const unsupported = useRef<boolean>(false);
  useEffect(() => {
    if (unsupported.current) return;

    // If no support of SharedArrayBuffer
    try {
      const sab = new SharedArrayBuffer(128);
      if (sab === undefined)
        throw new Error("SharedArrayBuffer is not supported");
    } catch {
      confirm(
        "record:UNSUPPORTED_SHAREDARRAYBUFFER",
        () => {
          navigate("./..");
          return Promise.resolve();
        },
        undefined,
        {
          title: t("record:UNSUPPORTED_BROWSER"),
        },
      );

      unsupported.current = true;
      return;
    }

    // Preload only: a failure is reported when the video is compressed
    loadFFmpeg().catch((e) => logger.error(e));
  }, [confirm, navigate, t, loadFFmpeg]);

  // `mediaLock` is used to not initialize the camera multiple times, this is the bridge between native and React
  const mediaLock = useRef<Promise<MediaStream> | null>(null);
  // `media` is the object that can be used in DOM / manipulations, it follow the React pattern
  const [media, setMedia] = useState<MediaStream | null>(null);

  const [recording, setRecording] = useState(false);
  const video = useRef(new Blob());
  const chunks = useRef<Blob[]>([]);
  const compressedVideo = useRef<Blob | null>(null);
  const [timer, setTimer] = useState<null | number>(null);
  const [timeElapsed, setTimeElapsed] = useState<null | number>(null);

  const [ffmpegPercent, setFFmpegPercent] = useState<number | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!timer) return setTimeElapsed(null);
      setTimeElapsed(Math.round((new Date().getTime() - timer) / 1000));
    }, timerInterval);

    return () => clearInterval(interval);
  }, [timer, timerInterval]);

  const videoFormat = useMemo(() => {
    let videoFormat: string;
    if (MediaRecorder.isTypeSupported("video/webm")) videoFormat = "webm";
    else if (MediaRecorder.isTypeSupported("video/mp4")) videoFormat = "mp4";
    else {
      logger.error("No supported video formats found");
      throw new Error("No supported video formats found");
    }
    return videoFormat;
  }, [MediaRecorder]);

  const mediaRecorder = useMemo(() => {
    if (!media) return null;

    // SOURCE: https://developer.mozilla.org/en-US/docs/Web/Media/Formats/Video_codecs
    // SOURCE: https://developer.mozilla.org/en-US/docs/Web/Media/Formats/Audio_codecs
    const mediaRecorder = new MediaRecorder(media, {
      mimeType: `video/${videoFormat}`,
    });

    return mediaRecorder;
  }, [videoFormat, media, MediaRecorder]);

  const startRecorder: RecorderService["startRecorder"] =
    useCallback(async () => {
      logger.info("asking to start recorder");
      if (unsupported.current) return null as any;

      // If the recorder is already initialized, just return the promise
      if (mediaLock.current) return mediaLock.current;

      // Else initialize and return the promise **instantly**
      // We do not want to await anything before setting the lock with a promise
      mediaLock.current = new Promise((resolve, reject) => {
        logger.info("initializing recorder");

        const constraints: MediaStreamConstraints = {
          audio: true,
          video: {
            height: {
              max: MAX_HEIGHT,
            },
            frameRate: FRAMERATE,
            facingMode: "user",
          },
        };

        // We need to wait for the user to allow access to the camera
        void navigator.mediaDevices.getUserMedia(constraints).then(
          (media) => {
            setMedia(media);
            resolve(media);
          },
          (e) => {
            // Do not keep the rejected promise: the next call must ask again
            mediaLock.current = null;
            toastGetUserMediaError(e);
            reject(e);
          },
        );
      });

      return mediaLock.current;
    }, []);

  const stopRecorder: RecorderService["stopRecorder"] = useCallback(() => {
    if (!mediaLock.current) {
      logger.info("recorder was not started");
      setMedia(null);
    } else {
      const mediaPromise = mediaLock.current;
      mediaLock.current = null;

      logger.info("stopping recorder");
      void mediaPromise.then((media) => {
        media.getTracks().forEach((track) => track.stop());
        setMedia(null);
      });
    }
  }, []);

  const onStart: RecorderService["onStart"] = useCallback(() => {
    if (!mediaRecorder) return false;
    try {
      mediaRecorder.start(chunkDuration);
      return true;
    } catch (e) {
      // e.g. NotSupportedError when the camera stream ended meanwhile
      logger.error(e);
      toastError("record:record.COULD_NOT_START", {
        id: "record.COULD_NOT_START",
      });
      return false;
    }
  }, [chunkDuration, mediaRecorder]);

  const onStop: RecorderService["onStop"] = useCallback(() => {
    return new Promise((resolve) => {
      function resolveOnStop() {
        mediaRecorder?.removeEventListener("stop", resolveOnStop);

        // The last `dataavailable` is always dispatched before `stop`
        video.current = new Blob(chunks.current, {
          type: mediaRecorder?.mimeType,
        });

        // WEBM timings are broken on chrome so we need to fix it before playback
        const fixedVideoPromise =
          videoFormat === "webm"
            ? fixWebmDuration(video.current).catch((e) => {
                // Some WEBM files (e.g. from Safari) cannot be parsed by this
                // lib, ffmpeg rewrites the timings when compressing anyway
                logger.error(e);
                return video.current;
              })
            : Promise.resolve(video.current);

        void fixedVideoPromise.then((fixedVideo) => {
          video.current = fixedVideo;
          resolve(fixedVideo);
        });
      }
      stopRecorder();
      setTimer(null);
      // Already stopped by the browser (e.g. camera unplugged): `stop` won't
      // be dispatched again and calling `stop()` would throw
      if (!mediaRecorder || mediaRecorder.state === "inactive")
        return resolveOnStop();

      mediaRecorder.addEventListener("stop", resolveOnStop);
      mediaRecorder.stop();
    });
  }, [mediaRecorder, stopRecorder, videoFormat]);

  useEffect(() => {
    if (!mediaRecorder) return;

    mediaRecorder.onstart = function () {
      logger.info("recorder started");
      chunks.current = [];
      setTimer(new Date().getTime());
      setRecording(true);
    };
    mediaRecorder.onstop = function () {
      logger.info("recorder stopped");
      setRecording(false);
    };

    mediaRecorder.ondataavailable = function (blobEvent) {
      if (blobEvent.data && blobEvent.data.size > 0) {
        logger.debug("recorder received data status: ", this.state);
        // Kept synchronous: an async concat would lose the last chunks
        chunks.current.push(blobEvent.data);
      }
    };
  }, [mediaRecorder, onStop, videoFormat]);

  const onCompress: RecorderService["onCompress"] = useCallback(
    async (optionalVideo, optionalDuration) => {
      let duration: number | null = optionalDuration ?? null;
      const videoToUpload = optionalVideo ?? video.current;

      logger.info("waiting for ffmpeg");

      const ffmpeg = await loadFFmpeg();

      logger.info("encoding video");
      const start = new Date().getTime();

      ffmpeg.setLogger(logger.debug);
      ffmpeg.setProgress((progress: any) => {
        if (progress.ratio === 0 && progress.duration > 0)
          duration = Math.max(progress.duration, optionalDuration ?? 0);
        logger.debug(duration, "progress", progress);

        const percent = Math.min(
          !!duration && !!progress.time
            ? Math.max(0, progress.time) / duration
            : Infinity,
          progress.ratio,
        );
        setFFmpegPercent(Math.max(0, Math.round(percent * 100)));
      });

      ffmpeg.FS(
        "writeFile",
        `video.${videoFormat}`,
        new Uint8Array(await videoToUpload.arrayBuffer()),
      );
      // https://trac.ffmpeg.org/wiki/Encode/H.264
      await ffmpeg.run(
        "-y",
        "-i",
        `video.${videoFormat}`,
        "-acodec",
        "aac",
        "-vcodec",
        "libx264",
        // Faster to encode/compress => Heavier in weight
        // Slower to encode/compress => Lighter in weight
        "-preset",
        // videoToUpload.size > maxUncompressedSize ? "superfast" : "ultrafast",
        "ultrafast",
        // Framerate in FPS
        "-r",
        "24",
        "-vf",
        // iw > ih => landscape
        // ih > iw => portrait
        // -2 => keep ratio in base 2 (! Required !)
        // \\ required to escape ','
        `scale=-2:min(${MAX_HEIGHT}\\,ih)`,
        "-crf",
        // Quality of the video between 0 and 51
        // 0 is lossless
        // 23 is default
        // 51 is worst
        // Sane range is 17-28
        // Visually lossless is 17-18
        "26",
        // Support less options but a better compatibility with older devices
        "-profile:v",
        "baseline",
        // Flag to move the metadata to the front of the video
        "-movflags",
        "+faststart",
        "video-export.mp4",
      );
      logger.info(
        `Encoded in ${Math.ceil((new Date().getTime() - start) / 100) / 10}s`,
      );
      setFFmpegPercent(null);

      const data = ffmpeg.FS("readFile", "video-export.mp4");
      if (data.length === 0) throw new Error("Failed to encode video");

      // Cleanup temp files
      ffmpeg.FS("unlink", `video.${videoFormat}`);
      ffmpeg.FS("unlink", "video-export.mp4");

      compressedVideo.current = new Blob([data], {
        type: "video/mp4",
      });

      return compressedVideo.current;
    },
    [videoFormat, loadFFmpeg],
  );

  const onUpload: RecorderService["onUpload"] = useCallback(
    async <T extends { uploadLink: string }>(
      getUploadLink: () => Promise<T>,
      optionalVideo?: Blob,
      optionalDuration?: number,
    ) => {
      let videoToUpload: Blob;
      if (compressedVideo.current) {
        videoToUpload = compressedVideo.current;
      } else {
        videoToUpload = await onCompress(optionalVideo, optionalDuration);
      }

      const unplayableBlob = new Blob([
        new Uint8Array(await videoToUpload.arrayBuffer()).reverse(),
      ]);

      const upload = await getUploadLink();
      logger.info(
        `Uploading a ${Math.ceil((videoToUpload.size / 1024 / 1024) * 10) / 10}MB file`,
      );
      const result = await fetch(upload.uploadLink, {
        method: "PUT",
        body: unplayableBlob,
        headers: {
          "Content-Type": "binary/octet-stream",
        },
      });
      // `fetch` does not reject on HTTP errors (e.g. expired signed link)
      if (!result.ok)
        throw new Error(`Upload failed with status ${result.status}`);

      return upload;
    },
    [onCompress],
  );

  const onExport: RecorderService["onExport"] = useCallback(
    (name) => {
      const blobUrl = URL.createObjectURL(video.current);
      downloadURL(blobUrl, `${name}.mp4`);
      logger.info(
        `Downloading a ${
          Math.ceil((video.current.size / 1024 / 1024) * 10) / 10
        }MB file`,
      );
      URL.revokeObjectURL(blobUrl);
    },
    [video],
  );

  return {
    isRecorderReady: useMemo(() => !!media, [media]),
    timeElapsed,
    recording,
    ffmpegPercent,
    startRecorder,
    stopRecorder,
    onStop,
    onStart,
    onExport,
    onUpload,
    onCompress,
  };
}

export function ProvideRecorder({
  children,
  chunkDuration,
  timerInterval,
  maxUncompressedSize,
}: PropsWithChildren<{
  timerInterval?: number;
  chunkDuration?: number;
  maxUncompressedSize?: number;
}>) {
  const recorderProvider = useProvideRecorder(
    timerInterval,
    chunkDuration,
    maxUncompressedSize,
  );

  // On unmount stop recorder
  useEffect(() => {
    return () => recorderProvider.stopRecorder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!navigator.mediaDevices) return "Unsupported navigator";

  return (
    <RecorderContext.Provider value={recorderProvider}>
      {children}
    </RecorderContext.Provider>
  );
}

export const VideoPreview = () => {
  const { startRecorder } = useRecorder()!;

  // `useRef` and not `createRef`: a new ref on each render would re-run the
  // effect, and ask for the camera again after a failure
  const videoPlayback = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!videoPlayback.current?.srcObject)
      startRecorder().then(
        (m) => {
          if (videoPlayback.current) videoPlayback.current.srcObject = m;
        },
        () => {
          // Already reported to the user by `startRecorder`
        },
      );
  }, [startRecorder]);

  return (
    <video
      className="videoplayer"
      playsInline
      autoPlay
      muted
      ref={videoPlayback}
      controlsList="nodownload"
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export function useRecorder() {
  return useContext(RecorderContext);
}
